if (!customElements.get('minimal-start-layout')) {
  customElements.define('minimal-start-layout', class extends HTMLElement {
    static resolve(bounds, logo, menu, logoPosition, menuPosition, gap, preservePositions = false) {
      const clamp = (value, min, max) => Math.max(min, Math.min(value, Math.max(min, max)));
      const result = {
        logoX: clamp(logo.left, bounds.left, bounds.right - logo.width),
        logoY: clamp(logo.top, bounds.top, bounds.bottom - logo.height),
        menuX: menuPosition === 'original' ? menu.left : clamp(menu.left, bounds.left, bounds.right - menu.width),
        menuY: clamp(menu.top, bounds.top, bounds.bottom - menu.height)
      };
      const overlapsX = result.logoX < result.menuX + menu.width + gap && result.logoX + logo.width + gap > result.menuX;
      const overlapsY = result.logoY < result.menuY + menu.height + gap && result.logoY + logo.height + gap > result.menuY;
      if (overlapsX && overlapsY) {
        const height = logo.height + gap + menu.height;
        const logoZone = logoPosition.split('_')[0];
        const menuZone = menuPosition.split('_')[0];
        let top = result.logoY;
        if (!preservePositions && logoZone === menuZone && logoZone === 'middle') {
          top = bounds.top + (bounds.bottom - bounds.top - height) / 2;
        } else if (!preservePositions && logoZone === menuZone && logoZone === 'bottom') {
          top = bounds.bottom - height;
        } else if (!preservePositions && logoZone === menuZone && logoZone === 'top') {
          top = Math.min(result.logoY, result.menuY);
        }
        result.logoY = clamp(top, bounds.top, bounds.bottom - height);
        result.menuY = result.logoY + logo.height + gap;
      }
      return result;
    }

    static placeGroup(bounds, logo, menu, position, offset, centerY, gap) {
      const clamp = (value, min, max) => Math.max(min, Math.min(value, Math.max(min, max)));
      const [vertical, horizontal] = position === 'original' ? ['middle', 'center'] : position.split('_');
      const width = Math.max(logo.width, menu.width);
      const height = logo.height + gap + menu.height;
      const x = horizontal === 'left' ? bounds.left : horizontal === 'right' ? bounds.right - width : (bounds.left + bounds.right - width) / 2;
      const y = vertical === 'top' ? bounds.top : vertical === 'bottom' ? bounds.bottom - height : centerY - height / 2;
      const left = clamp(x, bounds.left, bounds.right - width);
      const top = clamp(y + offset, bounds.top, bounds.bottom - height);
      return {
        logoX: left + (width - logo.width) / 2,
        logoY: top,
        menuX: left + (width - menu.width) / 2,
        menuY: top + logo.height + gap
      };
    }

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.logo = this.querySelector('.minimal-campaign__brand-placement');
      this.menu = this.querySelector('.minimal-campaign__menu-placement');
      if (!this.logo || !this.menu) return;
      this.social = this.closest('[data-campaign-entry]')?.querySelector('.minimal-campaign__social');
      const { signal } = this.controller;
      this.schedule = () => {
        if (this.frame || !this.isConnected || signal.aborted) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.layout();
        });
      };
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.schedule);
        [this, this.logo, this.menu, this.social].filter(Boolean).forEach((node) => this.observer.observe(node));
      }
      window.addEventListener('resize', this.schedule, { signal });
      window.addEventListener('pageshow', this.schedule, { signal });
      this.addEventListener('load', this.schedule, { capture: true, signal });
      document.fonts?.ready.then(this.schedule);
      this.layout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    layout() {
      const breakpoint = window.matchMedia('(max-width: 749px)').matches ? 'Mobile' : 'Desktop';
      const logoPosition = this.logo.dataset[`layout${breakpoint}`] || 'original';
      const menuPosition = this.menu.dataset[`layout${breakpoint}`] || 'original';
      [this.logo, this.menu].forEach((node) => {
        ['--placement-adjust-x', '--placement-adjust-y', '--placement-logo-max-height', '--placement-menu-max-height', '--placement-max-width'].forEach((property) => node.style.removeProperty(property));
      });
      this.menu.removeAttribute('data-menu-scroll');
      const frame = this.getBoundingClientRect();
      if (!frame.width || !frame.height) return;
      const style = getComputedStyle(this);
      const bounds = {
        left: frame.left + parseFloat(style.paddingLeft),
        right: frame.right - parseFloat(style.paddingRight),
        top: frame.top + parseFloat(style.paddingTop),
        bottom: frame.bottom - parseFloat(style.paddingBottom)
      };
      const centered = !this.closest('[data-campaign-entry]')?.classList.contains('minimal-campaign--row');
      const gap = centered ? 40 : breakpoint === 'Mobile' ? 16 : 24;
      const social = this.social?.getBoundingClientRect();
      if (social?.height) bounds.bottom = Math.min(bounds.bottom, social.top - gap);
      const height = Math.max(0, bounds.bottom - bounds.top);
      if (height < 80) return;
      this.logo.style.setProperty('--placement-max-width', `${bounds.right - bounds.left}px`);
      let logo = this.logo.getBoundingClientRect();
      let menu = this.menu.getBoundingClientRect();
      const maxMenuHeight = Math.max(44, height - Math.min(logo.height, 32) - gap);
      if (menu.height > maxMenuHeight) {
        this.menu.style.setProperty('--placement-menu-max-height', `${maxMenuHeight}px`);
        this.menu.setAttribute('data-menu-scroll', '');
        menu = this.menu.getBoundingClientRect();
      }
      if (logo.height + menu.height + gap > height) {
        this.logo.style.setProperty('--placement-logo-max-height', `${Math.max(24, height - menu.height - gap)}px`);
        logo = this.logo.getBoundingClientRect();
      }
      const logoOffset = parseFloat(getComputedStyle(this.logo).getPropertyValue('--placement-offset')) || 0;
      const menuOffset = parseFloat(getComputedStyle(this.menu).getPropertyValue('--placement-offset')) || 0;
      const neutralLogo = { left: logo.left, top: logo.top - logoOffset, width: logo.width, height: logo.height };
      const neutralMenu = { left: menu.left, top: menu.top - menuOffset, width: menu.width, height: menu.height };
      let result;
      if (centered) {
        result = this.constructor.placeGroup(bounds, neutralLogo, neutralMenu, logoPosition, logoOffset, frame.top + frame.height / 2, gap);
      } else {
        result = this.constructor.resolve(bounds, neutralLogo, neutralMenu, logoPosition, menuPosition, gap);
        result = this.constructor.resolve(bounds,
          { ...neutralLogo, left: result.logoX, top: result.logoY + logoOffset },
          { ...neutralMenu, left: result.menuX, top: result.menuY + menuOffset },
          logoPosition, menuPosition, gap, true);
      }
      this.logo.style.setProperty('--placement-adjust-x', `${result.logoX - logo.left}px`);
      this.logo.style.setProperty('--placement-adjust-y', `${result.logoY - logo.top}px`);
      this.menu.style.setProperty('--placement-adjust-x', `${result.menuX - menu.left}px`);
      this.menu.style.setProperty('--placement-adjust-y', `${result.menuY - menu.top}px`);
    }
  });
}
